import api from 'api/api'
import useBlockheight from 'api/useBlockheight'
import cn from 'classnames'
import styles from 'components/answers/answer-buttons.module.scss'
import SatsIcon from 'components/shared/SatsIcon'
import UserHandleFromId from 'components/shared/UserHandleFromId'
import Upvalue from 'components/votes/Upvalue'
import UpvalueGlyph from 'components/votes/UpvalueGlyph'
import { LARGE_UPVALUE_CONFIRM_MODAL_KEY } from 'components/votes/upvalueModalKeys'
import ReplyButton from 'components/shared/ReplyButton'
import { BITCOIN_UNIT, SATOSHIS, SATS_BEFORE_CONFIRM, USD_UNIT, format } from 'lib/bsv-util'
import { seedMinimumSats, seedingContextForAnswer } from 'lib/seeding'
import { Bitcoin, ChevronRight, Sprout } from 'lucide-react'
import React from 'react'
import ReactDOM from 'react-dom'
import useStore from 'state/knovStore'
import {
    reactOrUpvalue,
    nextPressIsFreeReaction,
    upvaluesEnabledForAnswer,
} from 'state/upvalueQueue'
import { answerWithSignalQuestSettings } from 'state/freeReactionEligibility'
import { DEFAULT_UPVALUE_EMOJI, isDefaultUpvalueEmoji } from 'components/votes/upvalueEmoji'
import type { Answer, IBsvLock, IBsvUpvalue, IReaction } from 'types/answers'
import { BSV_TX_STATES } from 'utils/bsvTransactionStatus'
import { bsvTxExplorerUrl } from 'utils/bsvExplorerUrls'
import BsvTransactionStatusIcon from 'components/shared/BsvTransactionStatusIcon'
import useBsvDisplay from 'components/shared/useBsvDisplay'
import { calculateValueSignalPopoverPosition } from 'components/answers/valueSignalPopoverPosition'
import {
    valueMeterFillBackground,
    valueMeterFillPercent,
    valueMeterSeedSharePercent,
} from 'lib/valueMeter'
import type { ValueSignalSafeArea } from 'components/answers/valueSignalPopoverPosition'

const createValueSignalSafeAreaProbe = (ownerDocument: Document) => {
    const probe = ownerDocument.createElement('div')
    probe.style.position = 'fixed'
    probe.style.visibility = 'hidden'
    probe.style.pointerEvents = 'none'
    probe.style.width = '0'
    probe.style.height = '0'
    probe.style.paddingTop =
        'max(env(safe-area-inset-top, 0px), var(--native-safe-area-top, 0px), var(--native-fullscreen-safe-area-top, 0px))'
    probe.style.paddingRight =
        'max(env(safe-area-inset-right, 0px), var(--native-safe-area-right, 0px))'
    probe.style.paddingBottom =
        'max(env(safe-area-inset-bottom, 0px), var(--native-safe-area-bottom, 0px), var(--native-fullscreen-safe-area-bottom, 0px), var(--native-system-controls-bottom, 0px))'
    probe.style.paddingLeft =
        'max(env(safe-area-inset-left, 0px), var(--native-safe-area-left, 0px))'
    ownerDocument.body.appendChild(probe)
    return probe
}

const readValueSignalSafeArea = (probe: HTMLElement): ValueSignalSafeArea => {
    const style = probe.ownerDocument.defaultView?.getComputedStyle(probe)
    return {
        top: parseFloat(style?.paddingTop || '0') || 0,
        right: parseFloat(style?.paddingRight || '0') || 0,
        bottom: parseFloat(style?.paddingBottom || '0') || 0,
        left: parseFloat(style?.paddingLeft || '0') || 0,
    }
}

/**
 * Derive the display state for an upvalue transaction when we do not yet have
 * the full BsvTx payload. This mirrors the legacy behaviour: spinner for the
 * first few seconds, pulsing block shortly after, and hourglass once the
 * operation appears stuck. If we already have a txId we treat it as awaiting
 * confirmation so the UI shows the standard block icon.
 */
function deriveUpvalueTxState(createdAt?: string, txId?: string | null, now: number = Date.now()) {
    if (!txId) {
        if (!createdAt) {
            return BSV_TX_STATES.PENDING
        }

        const createdMs = new Date(createdAt).getTime()
        const secondsElapsed = (now - createdMs) / 1000

        if (secondsElapsed > 40) {
            return BSV_TX_STATES.AWAITING_RESUBMISSION
        }

        return BSV_TX_STATES.PENDING
    }

    return BSV_TX_STATES.AWAITING_CONFIRMATION
}

function deriveSyntheticBsvTx(createdAt?: string, txId?: string | null, now: number = Date.now()) {
    const state = deriveUpvalueTxState(createdAt, txId, now)

    return {
        created_at: createdAt,
        status:
            state === BSV_TX_STATES.AWAITING_CONFIRMATION
                ? 'broadcasted'
                : state === BSV_TX_STATES.CONFIRMED
                ? 'confirmed'
                : 'pending',
        tx_id: txId,
    }
}

function groupUpvaluesBySentiment(upvalues: IBsvUpvalue[], reactions: IReaction[] = []) {
    const groups: Record<string, { amount: number; count: number }> = {}
    for (const upv of upvalues) {
        if (upv.is_seed) continue
        const emoji = upv?.metadata?.sentiment_emoji || DEFAULT_UPVALUE_EMOJI
        groups[emoji] = groups[emoji] || { amount: 0, count: 0 }
        groups[emoji].amount += parseFloat(String(upv.amount)) || 0
        groups[emoji].count += 1
    }
    // Free reactions count toward the emoji tally but carry no sats.
    for (const reaction of reactions) {
        const emoji = reaction?.emoji || DEFAULT_UPVALUE_EMOJI
        groups[emoji] = groups[emoji] || { amount: 0, count: 0 }
        groups[emoji].count += 1
    }

    return Object.entries(groups)
        .map(([emoji, { amount, count }]) => ({ emoji, amount, count }))
        .sort((a, b) => b.amount - a.amount || b.count - a.count)
}

// calculate total value signals (locks + upvalues) for an answer
export const calculateAnswerValueSignals = (answer: Answer, blockheight: number) => {
    const lockAmount = (answer?.bsv_locks || []).reduce((sum, lock) => {
        // only include locks that are at or below the current block height
        return lock.block_height >= blockheight ? sum + parseInt(lock.amount, 10) : sum
    }, 0)
    const upvalueAmount =
        answer?.bsv_upvalues?.reduce((sum, upvalue) => sum + parseInt(upvalue.amount, 10), 0) || 0
    return lockAmount + upvalueAmount
}

// seeds are a subset of upvalues; shown as their own level in the value display
export const calculateAnswerSeedSats = (answer: Answer) =>
    answer?.bsv_upvalues?.reduce(
        (sum, upvalue) => (upvalue.is_seed ? sum + parseInt(upvalue.amount, 10) : sum),
        0,
    ) || 0

export const calculateThreadSeedSats = (answers: Answer[]) =>
    answers?.reduce((acc, answer) => acc + calculateAnswerSeedSats(answer), 0) || 0

// calculate total value signals for multiple answers
export const calculateThreadValueSignals = (answers: Answer[], blockheight: number) => {
    return (
        answers?.reduce((acc, answer) => {
            return acc + calculateAnswerValueSignals(answer, blockheight)
        }, 0) || 0
    )
}

// get answers that have any value signals (locks or upvalues)
export const filterAnswersWithValueSignals = (answers: Answer[]) => {
    return (
        answers?.filter(
            answer => answer?.bsv_locks?.length > 0 || answer?.bsv_upvalues?.length > 0,
        ) || []
    )
}

export default function AnswerButtons({
    answer,
    quest = undefined,
    onReplyClick = undefined,
    quoteActive = false,
    // The reply lands in the composer on screen: keep the keyboard's focus.
    replyPreservesFocus = false,
    highlight = false,
    childCount = undefined,
    className = undefined,
    theme = undefined,
    // Thin controls (the Answer UI's row): the same controls in the compact ordinals-chat
    // presentation — value pill, borderless 28px upvalue/react/reply verbs.
    thin = false,
    // Only the bolt and amount, opening the breakdown: no meter, reactions or verbs
    // (a music card's player carries its own upvalue button).
    valueOnly = false,
    // Pointer over the value or the upvalue verbs (true) and off them (false):
    // the containing answer drops its hover highlight while a money control
    // carries the pointer.
    onHoverChange = undefined,
    // Artifact surface (ordinals dock): threads the ordinal context through to
    // the upvalue press so payouts route to the artifact's current owner.
    artifactOrdinalId = undefined,
    artifactOwnerId = undefined,
    upvalueSource = undefined,
    // Deferred surfaces can render the shared controls from a lightweight
    // answer summary, then resolve the canonical Answer before an action.
    resolveAnswer = undefined,
    displayValueSats = undefined,
    valueTestId = undefined,
    trailingControl = undefined,
    onOpenReactionPicker = undefined,
    onCloseReactionPicker = undefined,
    onReactionCommitted = undefined,
    onViewMintingPost = undefined,
}) {
    const postLockRef = React.useRef<HTMLDivElement>(null)
    const blockheight = useBlockheight()
    const [resolvedAnswer, setResolvedAnswer] = React.useState<Answer | null>(null)

    React.useEffect(() => {
        setResolvedAnswer(null)
    }, [answer.id])

    // Once the parent supplies the cached Answer it becomes authoritative so
    // optimistic cache updates keep flowing through this shared control row.
    const activeAnswer = resolveAnswer ? resolvedAnswer || answer : answer
    const signalAnswer = React.useMemo(
        () => answerWithSignalQuestSettings(activeAnswer, quest),
        [activeAnswer, quest],
    )
    const resolveSignalAnswer = React.useCallback(async (): Promise<Answer | null> => {
        if (resolvedAnswer || !resolveAnswer) return signalAnswer

        const nextAnswer = await resolveAnswer()
        if (!nextAnswer) return null

        setResolvedAnswer(nextAnswer)
        return answerWithSignalQuestSettings(nextAnswer, quest)
    }, [quest, resolveAnswer, resolvedAnswer, signalAnswer])
    const ownSats =
        resolvedAnswer || displayValueSats === undefined
            ? calculateAnswerValueSignals(signalAnswer, blockheight)
            : displayValueSats
    // An artifact wrapper displays its own value plus the value paid to the post
    // it was minted from — the same total the ordinals stream ranks it by. The
    // two are summed only here, at display: the upvalue pipeline keeps writing
    // to the wrapper's own amount, so optimistic updates can't double-count.
    const inheritedSats = Number(signalAnswer?.minting_bsv_upvalue_amt || 0)
    const lockedSats = ownSats + inheritedSats
    const seedSats = calculateAnswerSeedSats(signalAnswer)

    const [showPopover, _setShowPopover] = React.useState(false)
    const setShowPopover = async val => {
        if (val) {
            const nextAnswer = await resolveSignalAnswer()
            if (!nextAnswer) return

            // Standard answer surfaces still refresh before opening the
            // signal ledger. Deferred surfaces already resolved via the same
            // endpoint above.
            if (!resolveAnswer) await api.getAnswer(nextAnswer.id)
        }
        _setShowPopover(val)
    }

    return (
        <div
            className={cn(
                styles.bsvLockContainer,
                className,
                theme === 'play' && styles.playTheme,
                thin && styles.thinControls,
                valueOnly && styles.valueOnly,
            )}
        >
            <div
                className={styles.bsvLockAmountContainer}
                data-testid={valueTestId}
                data-value-sats={valueTestId ? lockedSats : undefined}
            >
                <>
                    <div
                        ref={postLockRef}
                        className={styles.lockAmountClickable}
                        data-answer-row-control
                        onMouseEnter={onHoverChange ? () => onHoverChange(true) : undefined}
                        onMouseLeave={onHoverChange ? () => onHoverChange(false) : undefined}
                        onClick={ev => {
                            ev.stopPropagation()
                            // Track opening value signals popup
                            window.logEv('VIEW_VALUE_SIGNALS_POPUP', {
                                answer_id: signalAnswer?.id,
                                total_sats: lockedSats,
                                num_upvalues: signalAnswer?.bsv_upvalues?.length || 0,
                                num_locks: signalAnswer?.bsv_locks?.length || 0,
                                context: 'answer',
                            })
                            setShowPopover(true)
                        }}
                    >
                        {valueOnly ? (
                            <LockAmount lockedSats={lockedSats} seedSats={0} compact />
                        ) : thin ? (
                            <ValueMeter
                                lockedSats={lockedSats}
                                seedSats={seedSats}
                                reactions={<SentimentSummary answer={signalAnswer} />}
                            />
                        ) : (
                            <>
                                <LockAmount lockedSats={lockedSats} seedSats={seedSats} />
                                <SentimentSummary answer={signalAnswer} />
                            </>
                        )}
                    </div>

                    {showPopover &&
                        ReactDOM.createPortal(
                            <BsvValueSignals
                                answers={[signalAnswer]}
                                postLockRef={postLockRef}
                                setShowPopover={setShowPopover}
                                onViewMintingPost={onViewMintingPost}
                            />,
                            document.body,
                        )}
                </>
            </div>

            {!valueOnly && (
                <div className={styles.rightControls}>
                    <div
                        className={styles.hoverScope}
                        data-answer-row-control
                        onMouseEnter={onHoverChange ? () => onHoverChange(true) : undefined}
                        onMouseLeave={onHoverChange ? () => onHoverChange(false) : undefined}
                    >
                        <Upvalue
                            postLockRef={postLockRef}
                            answer={signalAnswer}
                            signalQuest={quest}
                            theme={theme}
                            thin={thin}
                            artifactOrdinalId={artifactOrdinalId}
                            artifactOwnerId={artifactOwnerId}
                            source={upvalueSource}
                            resolveAnswer={resolveAnswer ? resolveSignalAnswer : undefined}
                            onOpenReactionPicker={onOpenReactionPicker}
                            onCloseReactionPicker={onCloseReactionPicker}
                            onReactionCommitted={onReactionCommitted}
                        />
                    </div>
                    {onReplyClick && (
                        <ReplyButton
                            onClick={onReplyClick}
                            quoteActive={quoteActive}
                            preserveFocus={replyPreservesFocus}
                            answer={signalAnswer}
                            highlight={highlight}
                            childCount={childCount}
                            thin={thin}
                        />
                    )}
                    {trailingControl}
                </div>
            )}
        </div>
    )
}

// Thin controls (the Answer UI's row): the value as a meter (lib/valueMeter). A fixed-length
// track whose fill reads as the value on a log scale, the seed share a denser
// tint feathered into the upvalue share, the end fading out. The figure is the
// total, with a sprout when part of it is seed; the reactions sit inside at
// the far end, right-aligned. The fill is measured against a fixed reference
// length, so when the figure and reactions need more room the track grows
// without changing the fill: the same value draws the same fill on every post.
function ValueMeter({
    lockedSats,
    seedSats = 0,
    reactions = null,
}: {
    lockedSats: number
    seedSats?: number
    reactions?: React.ReactNode
}) {
    const fillPercent = valueMeterFillPercent(lockedSats)
    const seedShare = valueMeterSeedSharePercent(lockedSats, seedSats)
    return (
        <span
            className={cn(
                styles.valueMeter,
                fillPercent === 0 && styles.valueMeterEmpty,
                seedShare >= 100 && styles.valueMeterAllSeed,
            )}
            data-value-meter-fill={fillPercent}
        >
            {fillPercent > 0 && (
                <span
                    className={styles.valueMeterFill}
                    style={{
                        // Sized against the meter's reference length, not the
                        // track: the track may grow to fit its figure and
                        // reactions without changing what the fill says.
                        width: `calc(var(--value-meter-length) * ${fillPercent / 100})`,
                        background: valueMeterFillBackground(seedShare),
                    }}
                    aria-hidden
                />
            )}
            <span className={styles.valueMeterLabel}>
                {seedShare > 0 && (
                    <Sprout
                        className={styles.valueMeterSprout}
                        size={12}
                        strokeWidth={2.2}
                        aria-hidden
                    />
                )}
                <LockAmount lockedSats={lockedSats} seedSats={0} />
            </span>
            <span className={styles.valueMeterReactions}>{reactions}</span>
        </span>
    )
}

// compact: sats rounded to K and M (dollars are already short); the breakdown has the
// exact figures.
function LockAmount({ lockedSats, seedSats = 0, compact = false }) {
    const { useUsd, formatSatsAmount } = useBsvDisplay()

    // seeds render as their own level; the base bars show the non-seed value
    const baseSats = Math.max(0, lockedSats - seedSats)
    const hasBitcoin = baseSats >= SATOSHIS

    const seedLevel =
        seedSats > 0 ? (
            <div className={styles.seedBar}>
                <Sprout className={styles.seedSymbol} size={13} aria-hidden />
                <div className={cn(styles.valueAmount, styles.seed)}>
                    {useUsd ? (
                        formatSatsAmount(seedSats, { includeUnit: false, compactSats: false })
                    ) : (
                        <NumberWithSpaces number={seedSats} px={2} />
                    )}
                </div>
            </div>
        ) : null

    if (useUsd) {
        return (
            <div className={styles.lockAmount}>
                {(baseSats > 0 || seedSats === 0) && (
                    <div className={cn(styles.usdBar, lockedSats === 0 && styles.emptyBar)}>
                        <div
                            className={cn(
                                styles.valueAmount,
                                styles.usd,
                                !lockedSats ? styles.dormant : null,
                            )}
                        >
                            {formatSatsAmount(baseSats, { includeUnit: false, compactSats: false })}
                        </div>
                    </div>
                )}
                {seedLevel}
            </div>
        )
    }

    if (compact && lockedSats > 0) {
        return (
            <div className={cn(styles.lockAmount, styles.bitcoinLockAmount)}>
                <div className={styles.satsBar}>
                    <SatsIcon showBars={true} contextStyles={styles.satsSymbol} />
                    <div className={cn(styles.valueAmount, styles.sats)}>{format(lockedSats)}</div>
                </div>
            </div>
        )
    }

    return (
        <div
            className={cn(
                styles.lockAmount,
                styles.bitcoinLockAmount,
                hasBitcoin && styles.hybridLockAmount,
            )}
        >
            {hasBitcoin && (
                <div className={styles.bitcoinBar}>
                    <Bitcoin className={styles.bitcoinSymbol} strokeWidth={3.4} aria-hidden />

                    <div
                        className={cn(
                            styles.valueAmount,
                            styles.bitcoin,
                            !lockedSats ? styles.dormant : null,
                        )}
                    >
                        <NumberWithSpaces number={Math.floor(baseSats / SATOSHIS)} px={2} />
                    </div>
                </div>
            )}

            {baseSats > 0 && baseSats % SATOSHIS > 0 && (
                <div className={styles.satsBar}>
                    <SatsIcon
                        showBars={true}
                        contextStyles={cn(
                            styles.satsSymbol,
                            !lockedSats ? styles.dormantSatsSymbol : null,
                        )}
                    />

                    <div
                        className={cn(
                            styles.valueAmount,
                            styles.sats,
                            !lockedSats ? styles.dormant : null,
                        )}
                    >
                        <>
                            <NumberWithSpaces number={baseSats % SATOSHIS} px={2} />
                        </>
                    </div>
                </div>
            )}

            {lockedSats === 0 && (
                <div className={styles.emptyBar}>
                    <SatsIcon
                        showBars={true}
                        contextStyles={cn(styles.satsSymbol, styles.dormantSatsSymbol)}
                    />

                    <div className={cn(styles.valueAmount, styles.sats, styles.dormant)}>0</div>
                </div>
            )}

            {seedLevel}
        </div>
    )
}

interface BsvValueSignalsProps {
    answers: Answer[] | Answer
    actionAnswer?: Answer
    upvalueAmountSats?: number
    seedAmountSats?: number
    postLockRef: React.RefObject<HTMLDivElement>
    setShowPopover: (open: boolean) => void
    onViewMintingPost?: () => void | Promise<void>
    elevated?: boolean
}

export function BsvValueSignals({
    answers,
    actionAnswer,
    upvalueAmountSats,
    seedAmountSats,
    postLockRef,
    setShowPopover,
    onViewMintingPost,
    elevated = false,
}: BsvValueSignalsProps) {
    const answersArray = Array.isArray(answers) ? answers : [answers]
    const storedUpvalueSats = useStore(state => state.upvalueSats)
    const storedSeedSats = useStore(state => state.seedSats)
    const upvalueSats = upvalueAmountSats ?? storedUpvalueSats
    const seedSats = seedAmountSats ?? storedSeedSats
    const signalType = useStore(state => state.signalType)
    const openAuthModal = useStore(state => state.actions.modalActions.openAuthModal)
    const { bsvUnit, useUsd, formatSatsAmount } = useBsvDisplay()
    const [activeSentimentFx, setActiveSentimentFx] = React.useState<string | null>(null)
    const sentimentFxTimeoutRef = React.useRef<number | null>(null)
    const popoverRef = React.useRef<HTMLDivElement>(null)
    const allUpvalues = answersArray.flatMap(ans => (ans?.bsv_upvalues || []) as IBsvUpvalue[])
    const allReactions = answersArray.flatMap(ans => (ans?.reactions || []) as IReaction[])
    const seedUpvalues = allUpvalues.filter(upvalue => upvalue.is_seed)
    const sentimentUpvalues = allUpvalues.filter(upvalue => !upvalue.is_seed)
    const totalUpvalues = sentimentUpvalues.length
    const totalSignals = totalUpvalues + allReactions.length
    // Artifact wrappers count value paid to the post they were minted from.
    // Those upvalues have their own ledger on that post, so the ledger below
    // names the amount rather than pretending to itemize it here.
    const inheritedSats = answersArray.reduce(
        (sum, ans) => sum + Number(ans?.minting_bsv_upvalue_amt || 0),
        0,
    )
    const upvalueTargetAnswer = actionAnswer || (answersArray.length === 1 ? answersArray[0] : null)
    const seedContext = upvalueTargetAnswer ? seedingContextForAnswer(upvalueTargetAnswer) : null
    const canSeedTarget = !!(seedContext && (seedContext.canChooseSeed || seedContext.forceSeed))
    const seedTotalSats = seedUpvalues.reduce(
        (sum, upvalue) => sum + (Number(upvalue.amount) || 0),
        0,
    )
    const totalValueSats =
        inheritedSats + allUpvalues.reduce((sum, upvalue) => sum + (Number(upvalue.amount) || 0), 0)
    const seedStakePercent = totalValueSats > 0 ? (seedTotalSats / totalValueSats) * 100 : 0
    const seeders = React.useMemo(() => {
        const totals = new Map<string, number>()
        for (const upvalue of seedUpvalues) {
            const userId = String(upvalue.from_user_id || '')
            totals.set(userId, (totals.get(userId) || 0) + (Number(upvalue.amount) || 0))
        }
        return Array.from(totals.entries())
            .map(([userId, amount]) => ({ userId, amount }))
            .sort((left, right) => right.amount - left.amount)
    }, [seedUpvalues])

    React.useEffect(
        () => () => {
            if (sentimentFxTimeoutRef.current) {
                window.clearTimeout(sentimentFxTimeoutRef.current)
            }
        },
        [],
    )

    React.useLayoutEffect(() => {
        const node = popoverRef.current
        const anchor = postLockRef.current
        if (!node || !anchor) return

        const ownerDocument = node.ownerDocument
        const ownerWindow = ownerDocument.defaultView || window
        const visualViewport = ownerWindow.visualViewport
        const safeAreaProbe = createValueSignalSafeAreaProbe(ownerDocument)
        const bottomNavigation = ownerDocument.querySelector<HTMLElement>(
            '[data-bottom-navigation]',
        )
        let frame = 0

        const positionPopover = () => {
            const anchorRect = anchor.getBoundingClientRect()
            const viewport = {
                left: visualViewport?.offsetLeft || 0,
                top: visualViewport?.offsetTop || 0,
                width: visualViewport?.width || ownerWindow.innerWidth,
                height: visualViewport?.height || ownerWindow.innerHeight,
            }
            const safeArea = readValueSignalSafeArea(safeAreaProbe)
            const navigationRect = bottomNavigation?.getBoundingClientRect()
            const viewportBottom = viewport.top + viewport.height
            const bottomObstructionTop =
                navigationRect &&
                bottomNavigation?.getClientRects().length > 0 &&
                navigationRect.top < viewportBottom &&
                navigationRect.bottom > viewport.top
                    ? navigationRect.top
                    : undefined
            const anchorGap = viewport.width <= 768 ? 16 : 5
            const basePosition = calculateValueSignalPopoverPosition({
                anchor: anchorRect,
                popover: { width: 0, height: 0 },
                viewport,
                safeArea,
                bottomObstructionTop,
                anchorGap,
            })

            node.style.maxWidth = `${basePosition.maxWidth}px`
            node.style.maxHeight = `${basePosition.maxHeight}px`
            node.style.minHeight = `${Math.min(200, basePosition.maxHeight)}px`

            const position = calculateValueSignalPopoverPosition({
                anchor: anchorRect,
                popover: { width: node.offsetWidth, height: node.offsetHeight },
                viewport,
                safeArea,
                bottomObstructionTop,
                anchorGap,
            })
            node.style.left = `${position.left}px`
            node.style.top = `${position.top}px`
        }
        const schedulePosition = () => {
            ownerWindow.cancelAnimationFrame(frame)
            frame = ownerWindow.requestAnimationFrame(positionPopover)
        }
        const resizeObserver =
            typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedulePosition)

        resizeObserver?.observe(node)
        resizeObserver?.observe(anchor)
        if (bottomNavigation) resizeObserver?.observe(bottomNavigation)
        ownerDocument.addEventListener('scroll', schedulePosition, true)
        ownerWindow.addEventListener('resize', schedulePosition)
        ownerWindow.addEventListener('orientationchange', schedulePosition)
        visualViewport?.addEventListener('resize', schedulePosition)
        visualViewport?.addEventListener('scroll', schedulePosition)
        positionPopover()

        return () => {
            ownerWindow.cancelAnimationFrame(frame)
            resizeObserver?.disconnect()
            safeAreaProbe.remove()
            ownerDocument.removeEventListener('scroll', schedulePosition, true)
            ownerWindow.removeEventListener('resize', schedulePosition)
            ownerWindow.removeEventListener('orientationchange', schedulePosition)
            visualViewport?.removeEventListener('resize', schedulePosition)
            visualViewport?.removeEventListener('scroll', schedulePosition)
        }
    }, [postLockRef])
    const sentimentBreakdown = React.useMemo(
        () => groupUpvaluesBySentiment(sentimentUpvalues, allReactions),
        [sentimentUpvalues, allReactions],
    )

    const handleSentimentUpvalue = (emoji: string) => {
        if (!upvalueTargetAnswer || !upvalueSats) return
        if (!gon?.currentUser) {
            openAuthModal('signup', 'sentiment_breakdown')
            return
        }
        const answerSignalType =
            upvalueTargetAnswer.user_id === gon.currentUser?.id && signalType === 'upvalue'
                ? 'boost'
                : signalType
        const sendSentimentUpvalue = () => {
            reactOrUpvalue(upvalueTargetAnswer as Answer, upvalueSats, answerSignalType, {
                reactionEmoji: emoji,
                sentimentSource: 'sentiment_breakdown',
                source: 'sentiment_breakdown',
            })
        }

        // No money moves on a free first reaction or in upvalue-disabled streams.
        const pressIsMonetary =
            !nextPressIsFreeReaction(upvalueTargetAnswer as Answer) &&
            upvaluesEnabledForAnswer(upvalueTargetAnswer as Answer)
        if (pressIsMonetary && upvalueSats > SATS_BEFORE_CONFIRM) {
            useStore
                .getState()
                .actions.modalActions.openConfirmModal(
                    `Upvalue ${formatSatsAmount(upvalueSats, { compactSats: false })}?`,
                    'Upvalue',
                    sendSentimentUpvalue,
                    { modalKey: LARGE_UPVALUE_CONFIRM_MODAL_KEY, disableOverlayBlur: true },
                )
        } else {
            sendSentimentUpvalue()
        }

        setActiveSentimentFx(emoji)
        if (sentimentFxTimeoutRef.current) {
            window.clearTimeout(sentimentFxTimeoutRef.current)
        }
        sentimentFxTimeoutRef.current = window.setTimeout(() => {
            setActiveSentimentFx(null)
        }, 360)
    }

    const handleSeed = () => {
        if (!upvalueTargetAnswer) return
        if (!gon?.currentUser) {
            openAuthModal('signup', 'seed_breakdown')
            return
        }
        if (!canSeedTarget || !upvaluesEnabledForAnswer(upvalueTargetAnswer)) {
            return
        }
        const amount = Math.max(seedMinimumSats(), seedSats || 0)
        const answerSignalType =
            upvalueTargetAnswer.user_id === gon.currentUser?.id && signalType === 'upvalue'
                ? 'boost'
                : signalType
        const sendSeed = () =>
            reactOrUpvalue(upvalueTargetAnswer, amount, answerSignalType, {
                isSeed: true,
                sentimentSource: 'sentiment_breakdown',
                source: 'sentiment_breakdown',
            })

        if (amount > SATS_BEFORE_CONFIRM) {
            useStore
                .getState()
                .actions.modalActions.openConfirmModal(
                    `Seed ${formatSatsAmount(amount, { compactSats: false })}?`,
                    'Seed',
                    sendSeed,
                    { modalKey: LARGE_UPVALUE_CONFIRM_MODAL_KEY, disableOverlayBlur: true },
                )
        } else {
            sendSeed()
        }
    }

    return (
        <div className={cn(styles.popover, elevated && styles.popoverElevated)} ref={popoverRef}>
            <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>
                    {totalSignals} {totalSignals === 1 ? 'Reaction' : 'Reactions'}
                </h3>
                <button
                    onClick={e => {
                        e.stopPropagation()
                        setShowPopover(false)
                    }}
                    className={styles.closeButton}
                    aria-label="Close modal"
                >
                    <i className="fa fa-times" />
                </button>
            </div>
            {seedTotalSats > 0 && (
                <div className={styles.seedStake}>
                    <div className={styles.seedStakeHeader}>
                        <span>
                            <strong>Seeded</strong>
                            <small>
                                {seeders.length} of {totalValueSats.toLocaleString()}
                            </small>
                        </span>
                        <b>
                            <Sprout size={14} strokeWidth={2} aria-hidden />
                            {seedTotalSats.toLocaleString()}
                        </b>
                    </div>
                    <div className={styles.seedStakeBarRow}>
                        <span className={styles.seedStakeBar}>
                            {seeders.map((seeder, index) => (
                                <i
                                    key={seeder.userId}
                                    style={{
                                        width: `${
                                            totalValueSats > 0
                                                ? (seeder.amount / totalValueSats) * 100
                                                : 0
                                        }%`,
                                        opacity: Math.max(0.25, 1 - index * 0.25),
                                    }}
                                />
                            ))}
                        </span>
                        <strong>{Math.round(seedStakePercent)}% staked</strong>
                    </div>
                    <div className={styles.seedStakeKey}>
                        {seeders.map((seeder, index) => (
                            <span key={seeder.userId}>
                                <i style={{ opacity: Math.max(0.25, 1 - index * 0.25) }} />
                                <UserHandleFromId
                                    userId={seeder.userId}
                                    afterClick={() => setShowPopover(false)}
                                />
                                {Math.round((seeder.amount / totalValueSats) * 100)}%
                            </span>
                        ))}
                        {seedStakePercent < 100 && (
                            <span>
                                <i className={styles.unstakedKey} />
                                unstaked {Math.max(0, Math.round(100 - seedStakePercent))}%
                            </span>
                        )}
                    </div>
                </div>
            )}
            {(seedTotalSats > 0 || canSeedTarget || sentimentBreakdown.length > 0) && (
                <div className={styles.sentimentBreakdown}>
                    {(seedTotalSats > 0 || canSeedTarget) && (
                        <button
                            type="button"
                            className={styles.seedSentimentItem}
                            onClick={ev => {
                                ev.stopPropagation()
                                handleSeed()
                            }}
                            disabled={!canSeedTarget}
                            title={
                                upvalueTargetAnswer
                                    ? 'Seed at your armed amount'
                                    : 'Seeding is available on single-answer popovers'
                            }
                        >
                            <Sprout size={15} strokeWidth={2} aria-hidden />
                            <span className={styles.sentimentAmount}>
                                {useUsd
                                    ? formatSatsAmount(seedTotalSats, {
                                          includeUnit: false,
                                          compactSats: false,
                                      })
                                    : seedTotalSats.toLocaleString()}
                            </span>
                            <span className={styles.sentimentCount}>
                                {seeders.length.toLocaleString()}
                            </span>
                        </button>
                    )}
                    {sentimentBreakdown.map(({ emoji, amount, count }) => (
                        <button
                            key={emoji}
                            type="button"
                            className={cn(
                                styles.sentimentItem,
                                activeSentimentFx === emoji && styles.sentimentItemFx,
                            )}
                            onClick={ev => {
                                ev.stopPropagation()
                                handleSentimentUpvalue(emoji)
                            }}
                            disabled={!upvalueTargetAnswer}
                            title={
                                upvalueTargetAnswer
                                    ? `Upvalue with ${emoji}`
                                    : 'Sentiment upvalue is available on single-answer popovers'
                            }
                        >
                            <UpvalueGlyph
                                emoji={emoji}
                                className={styles.sentimentEmoji}
                                size={16}
                            />
                            <span
                                className={cn(
                                    styles.sentimentAmount,
                                    amount <= 0 && styles.dormant,
                                )}
                            >
                                {useUsd
                                    ? formatSatsAmount(amount, {
                                          includeUnit: false,
                                          compactSats: false,
                                      })
                                    : Number(amount)?.toLocaleString()}
                            </span>
                            <span className={styles.sentimentCount}>{count.toLocaleString()}</span>
                        </button>
                    ))}
                </div>
            )}
            <div className={styles.locksList}>
                {inheritedSats > 0 && (
                    <div
                        className={styles.inheritedValueCard}
                        data-testid="inherited-minting-value"
                    >
                        <div className={styles.inheritedValueSignalIcon}>
                            <UpvalueGlyph emoji={DEFAULT_UPVALUE_EMOJI} size={18} />
                        </div>
                        <div className={styles.inheritedValueDetails}>
                            <div className={styles.inheritedValueAmount}>
                                {bsvUnit === BITCOIN_UNIT ? (
                                    <Bitcoin
                                        className={styles.inheritedValueBitcoinIcon}
                                        size={15}
                                        aria-hidden
                                    />
                                ) : !useUsd ? (
                                    <SatsIcon
                                        showBars={false}
                                        contextStyles={styles.inheritedValueSatsIcon}
                                    />
                                ) : null}
                                <span>
                                    {formatSatsAmount(inheritedSats, {
                                        includeUnit: false,
                                        compactSats: false,
                                    })}
                                </span>
                            </div>
                            <span className={styles.inheritedValueCaption}>
                                inherited from the minting post
                            </span>
                        </div>
                        {onViewMintingPost && (
                            <button
                                type="button"
                                className={styles.inheritedValueLink}
                                aria-label="View minting post"
                                onClick={ev => {
                                    ev.stopPropagation()
                                    setShowPopover(false)
                                    void onViewMintingPost()
                                }}
                            >
                                View
                                <ChevronRight size={13} aria-hidden />
                            </button>
                        )}
                    </div>
                )}
                <ul>
                    {allReactions.map((reaction, index) => (
                        <li key={`reaction-${index}`}>
                            <div className={styles.upvalueContent}>
                                <UserHandleFromId
                                    userId={reaction.user_id}
                                    afterClick={() => setShowPopover(false)}
                                />
                                <span className={styles.actionWord}>reacted</span>
                                <UpvalueGlyph
                                    emoji={reaction.emoji || DEFAULT_UPVALUE_EMOJI}
                                    className={styles.itemEmoji}
                                    size={18}
                                />
                            </div>
                        </li>
                    ))}
                    {allUpvalues.map((upvalue, index) => (
                        <li key={`upvalue-${index}`}>
                            {(() => {
                                const txId = upvalue.tx_id
                                const parentAnswer = answersArray.find(
                                    ans => ans.id === upvalue.answer_id,
                                )
                                const bsvTx =
                                    parentAnswer?.bsv_tx?.id === upvalue.bsv_tx_uuid
                                        ? parentAnswer.bsv_tx
                                        : null

                                const upvalueBsvTx =
                                    bsvTx || deriveSyntheticBsvTx(upvalue.created_at, txId)

                                return (
                                    <>
                                        <div className={styles.upvalueContent}>
                                            <UserHandleFromId
                                                userId={upvalue.from_user_id}
                                                afterClick={() => setShowPopover(false)}
                                            />
                                            <span
                                                className={
                                                    upvalue.is_seed
                                                        ? styles.seedActionWord
                                                        : styles.actionWord
                                                }
                                            >
                                                {upvalue.is_seed ? 'seeded' : 'upvalued'}
                                            </span>
                                            {upvalue.is_seed ? (
                                                <Sprout
                                                    className={styles.seedItemIcon}
                                                    size={15}
                                                    strokeWidth={2}
                                                    aria-hidden
                                                />
                                            ) : (
                                                <UpvalueGlyph
                                                    emoji={
                                                        upvalue?.metadata?.sentiment_emoji ||
                                                        DEFAULT_UPVALUE_EMOJI
                                                    }
                                                    className={styles.itemEmoji}
                                                    size={18}
                                                />
                                            )}
                                            {useUsd ? (
                                                <span className={styles.inlineUsdAmount}>
                                                    {formatSatsAmount(upvalue?.amount, {
                                                        includeUnit: false,
                                                        compactSats: false,
                                                    })}
                                                </span>
                                            ) : (
                                                <>
                                                    <SatsIcon
                                                        showBars={false}
                                                        contextStyles={styles.inlineSatsIcon}
                                                    />{' '}
                                                    {Number(upvalue?.amount)?.toLocaleString()}
                                                </>
                                            )}
                                        </div>
                                        <BsvTransactionStatusIcon
                                            bsvTx={upvalueBsvTx}
                                            fallbackTxId={txId}
                                            className={cn(
                                                'top-control blockchain-control',
                                                styles.txStatusIndicator,
                                            )}
                                            stateClassNameMap={{
                                                [BSV_TX_STATES.AWAITING_RESUBMISSION]:
                                                    styles.notOnchain,
                                                [BSV_TX_STATES.AWAITING_CONFIRMATION]:
                                                    styles.notOnchain,
                                            }}
                                            wrapperProps={{
                                                'data-upvalue-id': upvalue?.id ?? 'NOT_FOUND',
                                            }}
                                        />
                                    </>
                                )
                            })()}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    )
}

const NumberWithSpaces = ({ number, px }) => {
    let numberParts: string[] = []
    if (number === 0) {
        numberParts.push('0')
    } else {
        while (number > 0) {
            let part = number % 1000
            numberParts.unshift(part.toString().padStart(3, '0'))
            number = Math.floor(number / 1000)
        }
    }

    if (numberParts.length >= 1) {
        numberParts[0] = numberParts[0].replace(/^0+/, '')
    }

    return (
        <span>
            {numberParts.map((part, index) => (
                <React.Fragment key={index}>
                    {index > 0 && <span style={{ marginRight: `${px}px` }}></span>}
                    <span>{part}</span>
                </React.Fragment>
            ))}
        </span>
    )
}

function SentimentSummary({ answer }: { answer: Answer }) {
    const useUsd = useStore(state => state.bsvUnit === USD_UNIT)
    const sentiments = React.useMemo(() => {
        const upvalues = answer.bsv_upvalues || []
        return groupUpvaluesBySentiment(upvalues, answer.reactions || [])
    }, [answer.bsv_upvalues, answer.reactions])

    if (sentiments.length === 0) return null

    return (
        <div
            className={cn(styles.sentimentSummary, !useUsd && styles.bitcoinSentimentSummary)}
            title={sentiments.map(s => s.emoji).join('')}
        >
            {sentiments.map(s => (
                <UpvalueGlyph
                    key={s.emoji}
                    emoji={s.emoji}
                    className={styles.sentimentSummaryEmoji}
                    size={useUsd ? 16 : 14}
                />
            ))}
        </div>
    )
}

export { LockAmount, NumberWithSpaces, SentimentSummary }
